import axios, { AxiosHeaders } from 'axios';
import { AuthSessionEndedError, clearAuthToken, getAuthScope, getAuthToken, isAuthTokenDue, refreshAuthToken, } from './api';
const SKIPPED_URLS = /\/(rest-auth\/(login|logout|token\/refresh-cookie)|auth\/google)\/?(\?|$)/;
const inFlightRefreshes = new Map();
let installedOptions = null;
function sentToken(config) {
    const header = config.headers?.get('Authorization');
    if (typeof header !== 'string') {
        return null;
    }
    return header.replace(/^(Token|Bearer)\s+/, '') || null;
}
function classify(error) {
    const body = error.response?.data;
    if (error.response?.status !== 401) {
        return null;
    }
    if (body?.code === 'user_inactive') {
        return 'inactive';
    }
    if (body?.code !== 'token_not_valid') {
        return null;
    }
    // SimpleJWT's message is translatable; a non-matching translation degrades to a clean logout.
    const expired = body.messages?.some(m => m.token_type === 'access' && /expired/i.test(m.message || ''));
    return expired ? 'expired' : 'invalid';
}
function refreshOnce(options, failedToken) {
    const scope = getAuthScope();
    let pending = inFlightRefreshes.get(scope);
    if (!pending) {
        pending = refreshAuthToken()
            .then(resp => {
            if (!resp?.token) {
                throw new Error('Refresh cookie did not return an access token');
            }
            options.onTokenRefreshed?.(resp.token);
            return resp.token;
        })
            .catch(refreshError => {
            // Network errors, timeouts and 5xx (e.g. during a pb-api rollout) must not log users out.
            if (isSessionRejection(refreshError)) {
                endSession(options, failedToken);
            }
            throw refreshError;
        })
            .finally(() => inFlightRefreshes.delete(scope));
        inFlightRefreshes.set(scope, pending);
    }
    return pending;
}
function isSessionRejection(error) {
    return axios.isAxiosError(error) && error.response?.status === 401;
}
/** Renews a due token; on a temporary failure keeps using `current`, which is still inside its lifetime. */
async function renewBeforeSending(options, current) {
    try {
        return await refreshOnce(options, current);
    }
    catch (refreshError) {
        if (isSessionRejection(refreshError) || refreshError instanceof AuthSessionEndedError) {
            throw refreshError;
        }
        return current;
    }
}
function endSession(options, failedToken) {
    if (getAuthToken() !== failedToken) {
        return;
    }
    clearAuthToken();
    options.onSessionExpired();
}
function replay(config, token) {
    const headers = new AxiosHeaders(config.headers).set('Authorization', `Token ${token}`);
    const retry = { ...config, headers, _pbAuthRetry: true };
    return axios.request(retry);
}
/**
 * Renews the access token from the scoped refresh cookie shortly before it
 * expires, and replays a request rejected for an expired token once. Applies to
 * every caller of the default axios instance.
 */
export function installAuthRefreshInterceptor(options) {
    if (installedOptions) {
        return () => undefined;
    }
    installedOptions = options;
    // Vendor answers an expired pb token with a 403 the response interceptor cannot recognize.
    const requestId = axios.interceptors.request.use(async (config) => {
        const current = getAuthToken();
        if (!current || sentToken(config) !== current || SKIPPED_URLS.test(config.url || '') || !isAuthTokenDue()) {
            return config;
        }
        const fresh = await renewBeforeSending(options, current);
        config.headers = AxiosHeaders.from(config.headers).set('Authorization', `Token ${fresh}`);
        return config;
    });
    const responseId = axios.interceptors.response.use(undefined, async (error) => {
        const config = error.config;
        const kind = classify(error);
        const token = config ? sentToken(config) : null;
        if (!config || !kind || !token || SKIPPED_URLS.test(config.url || '')) {
            throw error;
        }
        if (kind === 'inactive') {
            endSession(options, token);
            throw error;
        }
        if (config._pbAuthRetry) {
            throw error;
        }
        if (kind === 'invalid') {
            endSession(options, token);
            throw error;
        }
        const current = getAuthToken();
        if (!current) {
            throw error;
        }
        if (current !== token) {
            return replay(config, current);
        }
        let fresh;
        try {
            fresh = await refreshOnce(options, token);
        }
        catch (refreshError) {
            // A temporary failure must not look like "logged out" (401) to callers.
            const sessionGone = isSessionRejection(refreshError) || refreshError instanceof AuthSessionEndedError;
            throw sessionGone ? error : refreshError;
        }
        return replay(config, fresh);
    });
    return () => {
        axios.interceptors.request.eject(requestId);
        axios.interceptors.response.eject(responseId);
        installedOptions = null;
    };
}
/**
 * Returns the current access token, renewing it first when it is due. For
 * callers that send the token outside the default axios instance (fetch,
 * upload widgets, WebSocket subprotocols). Rejects, ending the session, when
 * the refresh cookie is rejected; a temporary failure returns the current token.
 */
export async function getFreshAuthToken() {
    const current = getAuthToken();
    if (!current || !installedOptions || !isAuthTokenDue()) {
        return current;
    }
    return renewBeforeSending(installedOptions, current);
}
